import {
  createInitialGame, generateLegalMoves, isInCheck, opposite, playMove, rowOf,
} from './engine';
import { openingBookMatch } from './openingBook';
import type { Difficulty, GameState, MoveRecord, PieceType, Side } from './types';

export type LessonVerdictTone = 'achieved' | 'developing' | 'review';

export interface LessonMetric {
  label: string;
  value: string;
}

export interface PostgameAnalysis {
  title: string;
  verdict: string;
  tone: LessonVerdictTone;
  summary: string;
  highlight: string;
  metrics: LessonMetric[];
}

interface MoveEvent {
  ply: number;
  move: MoveRecord;
  playerMove: boolean;
  gaveCheck: boolean;
  captureValue: number;
  materialBalance: number;
  crossedRiver: boolean;
  enemyCamp: boolean;
  opponentMobility: number | null;
  mobilityDrop: number;
  openingName: string | null;
  endgame: boolean;
}

const PIECE_VALUES: Record<PieceType, number> = {
  K: 0, A: 20, E: 20, H: 45, R: 90, C: 50, P: 10,
};

const TITLES: Record<Difficulty, string> = {
  beginner: '先看清，再落子',
  trader: '每次交換，都要知道代價',
  cannonhorse: '讓馬與炮說同一種語言',
  gambiteer: '犧牲必須換到時間',
  attacker: '先手要能落在王城',
  defender: '守住弱點，再等反擊',
  pawn: '讓小兵一步一步成勢',
  miser: '留子不是退縮，是保存力量',
  blockader: '不必吃子，也能讓路消失',
  scholar: '走進熟譜，再走出自己的棋',
  veteran: '子越少，每一步越要算到底',
  master: '攻守取捨，都要有理由',
};

function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}

function resultPhrase(result: 'win' | 'loss' | 'draw'): string {
  return result === 'win' ? '最後把優勢換成了勝局。' : result === 'draw' ? '最後守住了和局。' : '最後仍由對手收下這局。';
}

function verdict(achieved: boolean, result: 'win' | 'loss' | 'draw'): Pick<PostgameAnalysis, 'verdict' | 'tone'> {
  if (achieved) return { verdict: '命題完成', tone: 'achieved' };
  if (result === 'win') return { verdict: '勝中有課', tone: 'developing' };
  return { verdict: '值得再試', tone: 'review' };
}

function crossedRiver(side: Side, from: number, to: number): boolean {
  const before = rowOf(from);
  const after = rowOf(to);
  return side === 'red' ? before > 4 && after <= 4 : before < 5 && after >= 5;
}

function reachedEnemyCamp(side: Side, to: number): boolean {
  return side === 'red' ? rowOf(to) <= 2 : rowOf(to) >= 7;
}

function replayEvents(state: GameState, playerSide: Side): MoveEvent[] {
  let replay = createInitialGame(state.seed);
  let materialBalance = 0;
  let previousOpponentMobility = generateLegalMoves(replay.board, opposite(playerSide)).length;
  const events: MoveEvent[] = [];

  for (let index = 0; index < state.moves.length; index += 1) {
    if (replay.outcome !== 'ongoing') break;
    const move = state.moves[index]!;
    const playerMove = move.piece.side === playerSide;
    const openingName = playerMove ? openingBookMatch(replay, 'scholar', move) : null;
    replay = playMove(replay, move);
    const gaveCheck = isInCheck(replay.board, opposite(move.piece.side));
    const captureValue = move.captured ? PIECE_VALUES[move.captured.type] : 0;
    if (captureValue) materialBalance += playerMove ? captureValue : -captureValue;
    const opponentMobility = playerMove ? generateLegalMoves(replay.board, opposite(playerSide)).length : null;
    const mobilityDrop = opponentMobility === null ? 0 : Math.max(0, previousOpponentMobility - opponentMobility);
    if (opponentMobility !== null) previousOpponentMobility = opponentMobility;
    const nonGenerals = replay.board.filter((piece) => piece && piece.type !== 'K').length;
    events.push({
      ply: index + 1,
      move,
      playerMove,
      gaveCheck,
      captureValue,
      materialBalance,
      crossedRiver: playerMove && move.piece.type === 'P' && crossedRiver(playerSide, move.from, move.to),
      enemyCamp: playerMove && reachedEnemyCamp(playerSide, move.to),
      opponentMobility,
      mobilityDrop,
      openingName,
      endgame: nonGenerals <= 8,
    });
  }
  return events;
}

function highlightText(event: MoveEvent | undefined, reason: string): string {
  return event ? `第 ${event.ply} 手「${event.move.notation}」：${reason}` : '本局沒有足夠落子可標記；下一局將從第一手開始累積命題資料。';
}

export function analyzePostgame(
  difficulty: Difficulty,
  state: GameState,
  playerSide: Side,
  result: 'win' | 'loss' | 'draw',
): PostgameAnalysis {
  const events = replayEvents(state, playerSide);
  const playerEvents = events.filter((event) => event.playerMove);
  const opponentEvents = events.filter((event) => !event.playerMove);
  const playerCaptures = playerEvents.filter((event) => event.move.captured);
  const losses = opponentEvents.filter((event) => event.move.captured);
  const playerChecks = playerEvents.filter((event) => event.gaveCheck);
  const opponentChecks = opponentEvents.filter((event) => event.gaveCheck);
  const parries = playerEvents.filter((event) => events[event.ply - 2]?.gaveCheck && !events[event.ply - 2]?.playerMove);
  const exchanges = events.slice(1).filter((event, index) => event.move.captured && events[index]?.move.captured && event.playerMove !== events[index]?.playerMove).length;
  const captureValue = playerCaptures.reduce((sum, event) => sum + event.captureValue, 0);
  const lostValue = losses.reduce((sum, event) => sum + event.captureValue, 0);
  const finalBalance = captureValue - lostValue;
  const maxDeficit = Math.max(0, ...events.map((event) => -event.materialBalance));
  const majorLosses = losses.filter((event) => ['R', 'C', 'H'].includes(event.move.captured?.type ?? '')).length;
  const pawnMoves = playerEvents.filter((event) => event.move.piece.type === 'P');
  const pawnCrossings = pawnMoves.filter((event) => event.crossedRiver);
  const pawnCaptures = pawnMoves.filter((event) => event.move.captured);
  const horseMoves = playerEvents.filter((event) => event.move.piece.type === 'H');
  const cannonMoves = playerEvents.filter((event) => event.move.piece.type === 'C');
  const horseCannonThreats = [...horseMoves, ...cannonMoves].filter((event) => event.gaveCheck || event.move.captured).length;
  const enemyCampMoves = playerEvents.filter((event) => event.enemyCamp);
  const tacticalMoves = playerEvents.filter((event) => event.gaveCheck || event.move.captured);
  const mobilitySamples = playerEvents.filter((event) => event.opponentMobility !== null);
  const minMobility = Math.min(...mobilitySamples.map((event) => event.opponentMobility ?? 99), 99);
  const maxMobilityDrop = Math.max(0, ...mobilitySamples.map((event) => event.mobilityDrop));
  const bookMatches = playerEvents.filter((event) => event.openingName);
  const firstBookDeparture = playerEvents.find((event) => !event.openingName);
  const firstEndgame = events.find((event) => event.endgame);
  const endgamePlayerEvents = firstEndgame ? playerEvents.filter((event) => event.ply >= firstEndgame.ply) : [];
  const endgameCaptures = endgamePlayerEvents.filter((event) => event.move.captured);
  const bestCapture = [...playerCaptures].sort((a, b) => b.captureValue - a.captureValue || a.ply - b.ply)[0];
  const strongestRestriction = [...mobilitySamples].sort((a, b) => (a.opponentMobility ?? 99) - (b.opponentMobility ?? 99))[0];
  const commonMetrics = {
    material: { label: '子力差', value: signed(finalBalance) },
    captures: { label: '你吃子', value: String(playerCaptures.length) },
    checks: { label: '你將軍', value: String(playerChecks.length) },
  };

  let achieved = false;
  let summary = '';
  let highlight = highlightText(bestCapture ?? playerChecks[0] ?? playerEvents[0], '這一步最能代表你本局的主動選擇。');
  let metrics: LessonMetric[] = [commonMetrics.captures, commonMetrics.checks, commonMetrics.material];

  switch (difficulty) {
    case 'beginner':
      achieved = result !== 'loss' && (playerChecks.length > 0 || finalBalance >= 0);
      summary = `阿芽先看最直接的威脅：你送出 ${playerChecks.length} 次將軍，面對 ${opponentChecks.length} 次將軍並完成 ${parries.length} 次應將。${resultPhrase(result)}`;
      highlight = highlightText(playerChecks[0] ?? bestCapture ?? playerEvents[0], '先確認威脅，再把棋走到有作用的位置。');
      metrics = [commonMetrics.checks, { label: '成功應將', value: String(parries.length) }, commonMetrics.material];
      break;
    case 'trader':
      achieved = exchanges >= 2 && finalBalance >= 0;
      summary = `鐵算盤把帳攤開：你完成 ${exchanges} 次連續交換，取回 ${captureValue} 點子力、付出 ${lostValue} 點，收盤為 ${signed(finalBalance)}。${resultPhrase(result)}`;
      highlight = highlightText(bestCapture ?? playerEvents[0], `這步取得 ${bestCapture?.captureValue ?? 0} 點子力，是本局最重的一筆交換。`);
      metrics = [{ label: '連續交換', value: String(exchanges) }, { label: '取／失', value: `${captureValue}／${lostValue}` }, commonMetrics.material];
      break;
    case 'cannonhorse':
      achieved = horseMoves.length > 0 && cannonMoves.length > 0 && horseCannonThreats >= 2;
      summary = `雷駒看的是節奏：你的馬走了 ${horseMoves.length} 手、炮走了 ${cannonMoves.length} 手，其中 ${horseCannonThreats} 手形成吃子或將軍。${resultPhrase(result)}`;
      highlight = highlightText([...horseMoves, ...cannonMoves].find((event) => event.gaveCheck || event.move.captured) ?? playerEvents[0], '馬炮開始互相接力，威脅才不會只響一聲。');
      metrics = [{ label: '馬／炮手數', value: `${horseMoves.length}／${cannonMoves.length}` }, { label: '馬炮戰術手', value: String(horseCannonThreats) }, commonMetrics.material];
      break;
    case 'gambiteer': {
      const recovered = Math.max(0, maxDeficit + finalBalance);
      achieved = maxDeficit >= 20 && recovered >= maxDeficit;
      summary = `赤刃檢查犧牲是否有回音：你曾落後 ${maxDeficit} 點子力，之後追回 ${recovered} 點，終局材料差 ${signed(finalBalance)}。${resultPhrase(result)}`;
      highlight = highlightText(playerChecks[0] ?? bestCapture ?? playerEvents[0], '棄掉的子只有換成先手、將軍或回收子力，才算真正的投資。');
      metrics = [{ label: '最大落後', value: String(maxDeficit) }, { label: '追回子力', value: String(recovered) }, commonMetrics.material];
      break;
    }
    case 'attacker':
      achieved = playerChecks.length >= 2 || enemyCampMoves.length >= 4;
      summary = `烈風只認落到王城的先手：你送出 ${playerChecks.length} 次將軍，${enemyCampMoves.length} 手深入對方底線，合計 ${tacticalMoves.length} 手直接吃子或將軍。${resultPhrase(result)}`;
      highlight = highlightText(playerChecks[0] ?? enemyCampMoves[0] ?? bestCapture ?? playerEvents[0], '這一步把主動權直接送進對方王城。');
      metrics = [commonMetrics.checks, { label: '深入敵營', value: String(enemyCampMoves.length) }, { label: '戰術手', value: String(tacticalMoves.length) }];
      break;
    case 'defender':
      achieved = majorLosses <= 1 && (parries.length > 0 || opponentChecks.length <= 1);
      summary = `玄壁看你如何承受壓力：對手送出 ${opponentChecks.length} 次將軍，你完成 ${parries.length} 次應將，並失去 ${majorLosses} 枚車馬炮。${resultPhrase(result)}`;
      highlight = highlightText(parries[0] ?? bestCapture ?? playerEvents[0], '先解掉最急的威脅，再保留反擊所需的子力。');
      metrics = [{ label: '對手將軍', value: String(opponentChecks.length) }, { label: '成功應將', value: String(parries.length) }, { label: '大子損失', value: String(majorLosses) }];
      break;
    case 'pawn':
      achieved = pawnCrossings.length > 0 || pawnMoves.length >= 4;
      summary = `青穗數的是每一步小進展：你走兵 ${pawnMoves.length} 手，讓 ${pawnCrossings.length} 枚兵越過楚河漢界，兵卒完成 ${pawnCaptures.length} 次吃子。${resultPhrase(result)}`;
      highlight = highlightText(pawnCrossings[0] ?? pawnCaptures[0] ?? pawnMoves[0] ?? playerEvents[0], '小兵一旦過河，便從守土的種子變成向前的壓力。');
      metrics = [{ label: '走兵', value: String(pawnMoves.length) }, { label: '過河', value: String(pawnCrossings.length) }, { label: '兵卒吃子', value: String(pawnCaptures.length) }];
      break;
    case 'miser':
      achieved = losses.length <= 3 || finalBalance >= 0;
      summary = `金庫逐枚清點：你保存了 ${16 - losses.length} 枚起始棋子，失去 ${losses.length} 枚，其中大子 ${majorLosses} 枚；材料收盤 ${signed(finalBalance)}。${resultPhrase(result)}`;
      highlight = highlightText(bestCapture ?? playerEvents[0], '保留子力不是不動，而是讓每顆子完成值得的任務。');
      metrics = [{ label: '保留棋子', value: String(Math.max(0, 16 - losses.length)) }, { label: '大子損失', value: String(majorLosses) }, commonMetrics.material];
      break;
    case 'blockader':
      achieved = minMobility <= 20 || maxMobilityDrop >= 8;
      summary = `無渡不急著數吃子，而是數出口：你曾把對手壓到只剩 ${minMobility} 種合法走法，單回合最大縮減 ${maxMobilityDrop} 種。${resultPhrase(result)}`;
      highlight = highlightText(strongestRestriction ?? bestCapture ?? playerEvents[0], `這一步之後，對手可走選項降到 ${strongestRestriction?.opponentMobility ?? minMobility} 種。`);
      metrics = [{ label: '最低活動力', value: String(minMobility) }, { label: '最大壓縮', value: String(maxMobilityDrop) }, commonMetrics.captures];
      break;
    case 'scholar': {
      const departurePly = firstBookDeparture?.ply ?? null;
      achieved = bookMatches.length >= 2 && Boolean(departurePly) && playerEvents.length - bookMatches.length >= 3;
      summary = bookMatches.length
        ? `百譜認出你有 ${bookMatches.length} 手仍在熟悉譜線中；${departurePly ? `第 ${departurePly} 手起走出已知次序` : '全局仍沿著熟路'}。${resultPhrase(result)}`
        : `百譜從你的第一個回合便找不到完全相同的譜線；之後 ${playerEvents.length} 手都必須靠臨場判斷。${resultPhrase(result)}`;
      highlight = highlightText(firstBookDeparture ?? bookMatches.at(-1) ?? playerEvents[0], firstBookDeparture ? '這裡是你離開熟譜、開始為自己的棋負責的位置。' : '這一步仍能在百譜中找到前人的足跡。');
      metrics = [{ label: '熟譜重合', value: String(bookMatches.length) }, { label: '離譜手數', value: departurePly ? `第 ${departurePly} 手` : '未離譜' }, { label: '你的總手數', value: String(playerEvents.length) }];
      break;
    }
    case 'veteran':
      achieved = Boolean(firstEndgame) && (result !== 'loss' || endgameCaptures.length > 0);
      summary = firstEndgame
        ? `秋燈在第 ${firstEndgame.ply} 手點亮殘局：此後你走了 ${endgamePlayerEvents.length} 手，完成 ${endgameCaptures.length} 次吃子，終局材料差 ${signed(finalBalance)}。${resultPhrase(result)}`
        : `本局在結束前仍有較多子力，尚未進入八子以下的深殘局；材料收盤 ${signed(finalBalance)}。${resultPhrase(result)}`;
      highlight = highlightText(endgamePlayerEvents[0] ?? bestCapture ?? playerEvents[0], '從這裡開始，棋子越少，每個節拍越不能浪費。');
      metrics = [{ label: '進入殘局', value: firstEndgame ? `第 ${firstEndgame.ply} 手` : '尚未' }, { label: '殘局吃子', value: String(endgameCaptures.length) }, commonMetrics.material];
      break;
    case 'master':
      achieved = result === 'win';
      summary = `雲隱看整盤的取捨：你完成 ${playerCaptures.length} 次吃子、${playerChecks.length} 次將軍、${parries.length} 次應將，材料收盤 ${signed(finalBalance)}。${resultPhrase(result)}`;
      highlight = highlightText(bestCapture ?? playerChecks[0] ?? parries[0] ?? playerEvents[0], '這一步同時反映了你對先手、材料與王城安全的取捨。');
      metrics = [commonMetrics.captures, commonMetrics.checks, { label: '成功應將', value: String(parries.length) }];
      break;
  }

  return { title: TITLES[difficulty], ...verdict(achieved, result), summary, highlight, metrics };
}
